/**
 * Ordenação de chamados NO NAVEGADOR, igual à do servidor.
 *
 * ## Por que isto não pode ser um `sort` qualquer
 *
 * O quadro do Kanban monta cada coluna de **duas fontes**: os chamados que já
 * estão em memória (a lista carregada) e as páginas que o servidor manda por
 * (responsável × coluna), de dez em dez, conforme a pessoa pede "carregar
 * mais". As duas precisam da MESMA ordem — senão a coluna aparece ordenada
 * até a décima linha e embaralhada a partir dali, e ninguém entende por quê.
 *
 * Quem manda é o servidor, porque é ele que pagina: pedir "os dez próximos
 * por prioridade" só faz sentido se o navegador concordar com o que é "os dez
 * primeiros". Então este arquivo imita o `ORDER BY` do MySQL, e não o gosto do
 * JavaScript.
 *
 * ## As duas diferenças entre o `ORDER BY` do MySQL e o `sort` do JS
 *
 * **1. Onde ficam os nulos.** No MySQL, `ORDER BY col ASC` põe os nulos
 * PRIMEIRO e `DESC` põe por ÚLTIMO — nulo é o menor valor possível. O `sort`
 * do JS não tem opinião: comparar `null` com número dá `NaN` em qualquer
 * subtração, o comparador devolve `NaN`, e o resultado é ordem indefinida.
 * Isso importa de verdade em `resolved_at`, que é nulo em todo chamado que
 * ainda não foi resolvido — ou seja, na maioria.
 *
 * **2. O desempate.** Duas linhas com o mesmo valor podem sair em qualquer
 * ordem, e o MySQL não promete estabilidade entre páginas. Por isso o `id`
 * entra como critério final: com ele, "a página 2" é sempre a mesma página 2.
 *
 * As colunas aceitas são as mesmas de `TicketController::COLUNAS_ORDENAVEIS`,
 * e por lá a lista é fechada porque cada uma precisa de índice.
 */

/** As colunas que o servidor aceita ordenar. Espelha `COLUNAS_ORDENAVEIS`. */
export const COLUNAS_ORDENAVEIS = [
    'number',
    'created_at',
    'updated_at',
    'resolved_at',
    'priority',
    'status_id',
    'id',
];

/** O que a tela oferece, com o rótulo que a pessoa lê. */
export const OPCOES_DE_ORDENACAO = [
    { campo: 'updated_at', rotulo: 'Última atualização' },
    { campo: 'created_at', rotulo: 'Data de criação' },
    { campo: 'priority', rotulo: 'Prioridade' },
    { campo: 'number', rotulo: 'Número do chamado' },
    { campo: 'resolved_at', rotulo: 'Data de resolução' },
    { campo: 'status_id', rotulo: 'Status' },
];

/** A ordenação de sempre do quadro, mantida como padrão. */
export const ORDENACAO_PADRAO = { campo: 'updated_at', direcao: 'desc' };

const CAMPOS_DE_DATA = ['created_at', 'updated_at', 'resolved_at'];

/**
 * O valor comparável de um campo, ou `null`.
 *
 * Data vira número (milissegundos) para a comparação não cair no alfabético —
 * que acerta por acidente em ISO 8601 e erra em qualquer outro formato que a
 * api venha a mandar.
 */
const valorDe = (ticket, campo) => {
    const cru = ticket?.[campo];

    if (cru == null || cru === '') return null;

    if (CAMPOS_DE_DATA.includes(campo)) {
        const ms = new Date(cru).getTime();
        return Number.isNaN(ms) ? null : ms;
    }

    const n = Number(cru);
    return Number.isNaN(n) ? null : n;
};

/**
 * Compara dois chamados como o MySQL compararia.
 *
 * @param {string} campo    uma de `COLUNAS_ORDENAVEIS`
 * @param {'asc'|'desc'} direcao
 */
export const compararChamados = (campo, direcao) => {
    const desc = direcao === 'desc';
    const sinal = desc ? -1 : 1;

    return (a, b) => {
        const va = valorDe(a, campo);
        const vb = valorDe(b, campo);

        // Nulo é o MENOR valor, como no MySQL: primeiro no crescente, último
        // no decrescente. O `sinal` cuida das duas direções de uma vez.
        if (va === null && vb === null) {
            // Cai no desempate por id, abaixo.
        } else if (va === null) {
            return -1 * sinal;
        } else if (vb === null) {
            return 1 * sinal;
        } else if (va !== vb) {
            return (va < vb ? -1 : 1) * sinal;
        }

        // Desempate por id, SEMPRE na mesma direção da ordenação pedida: é o
        // que torna a página 2 do servidor igual à página 2 que o navegador
        // esperava. Sem ele, dois chamados atualizados no mesmo segundo
        // podem trocar de lugar entre uma rolagem e outra.
        const ia = Number(a?.id) || 0;
        const ib = Number(b?.id) || 0;

        if (ia === ib) return 0;

        return (ia < ib ? -1 : 1) * sinal;
    };
};

/**
 * Uma cópia ordenada da lista.
 *
 * Cópia, e não `sort` no lugar: a lista vem de `useMemo` sobre o estado, e
 * ordenar no lugar mutaria o array que o React guarda — o tipo de coisa que
 * funciona até a primeira renderização que depende da ordem anterior.
 *
 * Campo desconhecido devolve a lista intacta, em vez de inventar uma ordem:
 * se o servidor não ordena por aquele campo, o navegador também não pode.
 */
export const ordenarChamados = (chamados, campo, direcao) => {
    const lista = Array.isArray(chamados) ? chamados : [];

    if (!COLUNAS_ORDENAVEIS.includes(campo)) return lista;

    return [...lista].sort(compararChamados(campo, direcao));
};
